import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dotProp from 'dot-prop-immutable';
import { keyBy } from 'lodash';
import { TaskId } from 'api/models';
import { Task } from 'api/tasks';
import {
  EntityState,
  FetchStatus,
  NormalizedData,
  SavePayloadAction,
} from 'data/core';
import { saveNormalizedData } from 'data/core/utils';
import {
  AddMultipleSubtasksActionPayload,
  AddSubTaskActionPayload,
  boardTaskSavedAction,
  CreateMultipleTasksSuccessPayload,
  CreateSubTaskSuccessActionPayload,
  CreateTaskSuccessPayload,
} from './actions';
import { taskNormalizer } from './schema';
import { Task as NormalizedTask } from './index';

type TasksState = EntityState<NormalizedTask> & {
  lastBoardTaskSaved: { taskId: number; projectId: number } | null;
};

const initialState: TasksState = {
  items: {},
  itemsFetchStatus: {},
  isError: false,
  isLoading: false,
  lastBoardTaskSaved: null,
};

const TasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    saveTasks(
      state,
      { payload: { data, fetchStatus } }: SavePayloadAction<Task[]>,
    ) {
      if (data.length === 0) {
        return state;
      }
      const mainTaskIds = data.map((d) => d.taskId);
      const {
        entities: { tasks },
      } = taskNormalizer(data);

      const subTaskIds = Object.keys(tasks).filter(
        (t) => !mainTaskIds.includes(Number(t)),
      );

      mainTaskIds.forEach((id) => {
        state.items[id] = { ...state.items[id], ...tasks[id] };
        state.itemsFetchStatus[id] =
          state.itemsFetchStatus[id] === FetchStatus.Loaded
            ? FetchStatus.Loaded
            : fetchStatus;
      });
      subTaskIds.forEach((id) => {
        state.items[id] = { ...state.items[id], ...tasks[id] };
        state.itemsFetchStatus[id] =
          state.itemsFetchStatus[id] === FetchStatus.Loaded
            ? FetchStatus.Loaded
            : FetchStatus.Partial;
      });

      return state;
    },
    saveNormalizedTask(
      state,
      action: SavePayloadAction<NormalizedData<NormalizedTask>>,
    ): TasksState {
      return saveNormalizedData(state, action);
    },
    saveNormalizedTasks(
      state,
      action: SavePayloadAction<NormalizedData<NormalizedTask>>,
    ): TasksState {
      return saveNormalizedData(state, action);
    },
    deleteTask(state, { payload }: PayloadAction<TaskId>): TasksState {
      const [, , taskId] = payload;
      const task = dotProp.get(state.items, taskId);

      if (task) {
        let items = dotProp.delete(state.items, taskId);

        /**
         * Remove task reference from parent task object.
         */
        const { parentTask } = task;

        if (parentTask) {
          const parentTaskObject = dotProp.get(state.items, parentTask);

          if (parentTaskObject != null && parentTaskObject.subTasks != null) {
            items = dotProp.set(items, `${parentTask}`, {
              ...parentTaskObject,
              subTasks: parentTaskObject.subTasks.filter(
                (_taskId) => _taskId !== taskId,
              ),
            });
          }
        }

        return {
          ...state,
          items: {
            ...items,
          },
        };
      }

      return state;
    },
    saveNormalizedPartialTasks(
      state,
      action: PayloadAction<NormalizedData<NormalizedTask>>,
    ) {
      const { payload } = action;

      if (payload && Object.keys(payload)) {
        // We do not want to change Fully Loaded task status to partial.
        Object.keys(payload).forEach((taskId) => {
          state.items[taskId] = { ...state.items[taskId], ...payload[taskId] };
          state.itemsFetchStatus[taskId] =
            state.itemsFetchStatus[taskId] === FetchStatus.Loaded
              ? FetchStatus.Loaded
              : FetchStatus.Partial;
        });
      }

      return state;
    },
    addSubTaskToTask(
      state,
      { payload }: PayloadAction<Omit<AddSubTaskActionPayload, 'queryClient'>>,
    ): TasksState {
      const { taskId, parentTask, taskName, fields } = payload;

      let parentTaskObj = state.items?.[parentTask];
      const parentSubTasks = parentTaskObj?.subTasks ?? [];
      const subTaskOrder = parentTaskObj?.subTaskOrder ?? [];

      if (parentTaskObj) {
        parentTaskObj = {
          ...parentTaskObj,
          subTaskOrder: [...subTaskOrder, taskId],
          subTasks: [...parentSubTasks, taskId],
        };

        const newTask = {};

        newTask[taskId] = {
          taskName,
          taskId,
          fields,
          responsible: [],
        } as unknown as Task;

        return {
          ...state,
          items: {
            ...state.items,
            ...newTask,
            [parentTask]: {
              ...parentTaskObj,
            },
          },
        };
      }

      return state;
    },
    addMultipleSubTasksToTask(
      state,
      {
        payload,
      }: PayloadAction<Omit<AddMultipleSubtasksActionPayload, 'queryClient'>>,
    ): TasksState {
      const { parentTask, fields, subtasks } = payload;

      let parentTaskObj = state.items?.[parentTask];
      const parentSubTasks = parentTaskObj?.subTasks ?? [];
      const subTaskOrder = parentTaskObj?.subTaskOrder ?? [];
      const taskIds = subtasks.map((e) => e.taskId);

      if (parentTaskObj) {
        parentTaskObj = {
          ...parentTaskObj,
          subTaskOrder: [...subTaskOrder, ...taskIds],
          subTasks: [...parentSubTasks, ...taskIds],
        };

        const newTask: NormalizedData<NormalizedTask> = {};

        subtasks.forEach((subtask) => {
          newTask[subtask.taskId] = {
            taskName: subtask.taskName,
            taskId: subtask.taskId,
            fields,
          } as NormalizedTask;
        });

        return {
          ...state,
          items: {
            ...state.items,
            ...newTask,
            [parentTask]: {
              ...parentTaskObj,
            },
          },
        };
      }

      return state;
    },
    createSubTaskSuccess: (
      state: TasksState,
      { payload }: PayloadAction<CreateSubTaskSuccessActionPayload>,
    ): TasksState => {
      const { taskId, parentTask, task } = payload;

      const {
        entities: { tasks },
      } = taskNormalizer(task);

      const newState = dotProp.delete(state, `items.${taskId}`);
      let parentTaskObj: NormalizedTask = dotProp.get(
        newState.items,
        parentTask,
      );
      const subTasks =
        parentTaskObj?.subTasks?.filter?.((id) => id !== taskId) ?? [];

      parentTaskObj = {
        ...(parentTaskObj ?? {}),
        subTasks: [...subTasks, task.taskId],
        subTaskOrder: [...subTasks, task.taskId],
      };

      const result = dotProp.set(
        newState,
        `items.${parentTask}`,
        parentTaskObj,
      );

      return dotProp.merge(result, 'items', tasks);
    },
    createTaskSuccess: (
      state: TasksState,
      { payload }: PayloadAction<CreateTaskSuccessPayload>,
    ): TasksState => {
      const { tempTaskId, task } = payload;
      const result = dotProp.delete(state, `items.${tempTaskId}`);
      const {
        entities: { tasks },
      } = taskNormalizer(task);

      return dotProp.merge(result, 'items', tasks);
    },

    createMultipleTaskSuccess: (
      state: TasksState,
      { payload }: PayloadAction<CreateMultipleTasksSuccessPayload>,
    ): TasksState => {
      const { tasks: _tasks, subtasks } = payload;
      const result = subtasks.map((subtask) =>
        dotProp.delete(state, `items.${subtask.taskId}`),
      );
      const {
        entities: { tasks },
      } = taskNormalizer(_tasks);

      return dotProp.merge(result, 'items', tasks);
    },

    fetchTaskDetailStart: (
      state: TasksState,
      { payload }: PayloadAction<number>,
    ) => {
      return {
        ...state,
        itemsFetchStatus: {
          ...state.itemsFetchStatus,
          [payload]: FetchStatus.Loading,
        },
      };
    },
    fetchTaskDetailError: (
      state: TasksState,
      { payload }: PayloadAction<number>,
    ) => {
      return {
        ...state,
        itemsFetchStatus: {
          ...state.itemsFetchStatus,
          [payload]: FetchStatus.Error,
        },
      };
    },
    updateTaskPrivacy: (
      state: TasksState,
      { payload }: PayloadAction<{ taskId: number; private: boolean }>,
    ) => {
      const { taskId, private: isPrivate } = payload;
      const task = state.items[taskId];

      return {
        ...state,
        items: {
          ...state.items,
          [taskId]: {
            ...task,
            private: isPrivate,
          },
        },
      };
    },
    updateTasksDates: (
      state: TasksState,
      { payload }: PayloadAction<any[]>,
    ) => {
      const updatedTasks = {};

      payload.forEach(({ id, taskId, startDate, dueDate }) => {
        // const { id, taskId, startDate, dueDate } = data;
        const task = state.items[taskId || id];

        if (task) {
          updatedTasks[taskId || id] = {
            ...task,
            startDate: startDate || task.startDate,
            dueDate: dueDate || task.dueDate,
          };
        }
      });

      return {
        ...state,
        items: {
          ...state.items,
          ...updatedTasks,
        },
      };
    },
    createDependencies: (
      state: TasksState,
      { payload }: PayloadAction<{ data }[]>,
    ) => {
      const updatedTasks = {};

      payload.forEach(({ data }) => {
        const { taskId, dependentTaskId } = data;
        const task = state.items[taskId];
        const dependentTask = state.items[dependentTaskId];
        const dependentTasks = task?.dependentTasks
          ? [...task.dependentTasks, dependentTask]
          : [dependentTask];

        updatedTasks[taskId] = {
          ...task,
          dependentTasks,
        };
      });

      return {
        ...state,
        items: {
          ...state.items,
          ...updatedTasks,
        },
      };
    },
    deleteDependencies: (
      state: TasksState,
      { payload }: PayloadAction<{ data }[]>,
    ) => {
      const updatedTasks = {};

      payload.forEach(({ data }) => {
        const { taskId, dependentTaskId } = data;
        const task = state.items[taskId];
        const dependentTasks = task?.dependentTasks.filter(
          (_dTask) => _dTask.taskId !== dependentTaskId,
        );

        updatedTasks[taskId] = {
          ...task,
          dependentTasks,
        };
      });

      return {
        ...state,
        items: {
          ...state.items,
          ...updatedTasks,
        },
      };
    },
    removeProjectTasks: (
      state: TasksState,
      { payload }: PayloadAction<number[]>,
    ) => {
      const tasks = Object.values(state.items).filter(
        (task) => !payload.includes(task.project),
      );
      const items = keyBy(tasks, 'taskId');

      return {
        ...state,
        items,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(boardTaskSavedAction, (state, { payload }) => {
      state.lastBoardTaskSaved = {
        taskId: payload.taskId,
        projectId: payload.projectId,
      };
    });
  },
});

export const {
  deleteTask,
  saveTasks,
  addSubTaskToTask,
  addMultipleSubTasksToTask,
  createSubTaskSuccess,
  createMultipleTaskSuccess,
  saveNormalizedTask,
  createTaskSuccess,
  fetchTaskDetailStart,
  fetchTaskDetailError,
  updateTaskPrivacy,
  saveNormalizedPartialTasks,
  updateTasksDates,
  createDependencies,
  deleteDependencies,
  removeProjectTasks,
} = TasksSlice.actions;
export const tasksReducer = TasksSlice.reducer;
